import {Injectable} from '@angular/core';
import {AlertButton, AlertController} from '@ionic/angular';

export type TypedConfirmationResult = 'confirmed' | 'cancelled' | 'invalid';
type ConfirmationHandler = () => ReturnType<NonNullable<AlertButton['handler']>>;

/** Pulsante di `presentChoiceAlert`: `value` è quanto restituito se viene premuto. */
export interface SceltaAlert<T extends string> {
  text: string;
  value: T;
  /** Suffisso della classe `alert-button-confirm-<cssClass>`, come in `presentCustomAlert`. */
  cssClass?: string;
}

@Injectable({providedIn: 'root'})
export class MessageConfirm {
  constructor(private alertController: AlertController) {
  }

  async presentAlert(message: string, handleConfirm: ConfirmationHandler): Promise<boolean> {
    return this.presentCustomAlert('Conferma', message, 'OK', 'success', handleConfirm);
  }

  // `cancelBtn` è opzionale e resta 'Annulla' per tutti i chiamanti esistenti: serve dove la
  // scelta non è "annulla / conferma" ma due alternative entrambe legittime (es. mantenere il
  // periodo filtrato oppure ampliarlo), dove "Annulla" descriverebbe male cosa fa il pulsante.
  async presentCustomAlert(title: string, message: string, confirmBtn: string, cssClass: string,
                           handleConfirm: ConfirmationHandler, cancelBtn = 'Annulla'): Promise<boolean> {
    // Toglie il focus dall'elemento che ha aperto l'alert prima che l'overlay lo copra:
    // altrimenti resta un elemento focused sotto aria-hidden (warning di accessibilità).
    this.blurActiveElement();
    const alert = await this.alertController.create({
      header: title,
      message: message,
      buttons: [
        {text: cancelBtn, role: 'cancel'},
        {
          text: confirmBtn,
          role: 'confirm',
          cssClass: `alert-button-confirm-${cssClass}`,
          handler: () => handleConfirm()
        }
      ],
      cssClass: cssClass
    });
    await alert.present();

    const {role} = await alert.onDidDismiss(); // aspetta la scelta dell'utente
    // Stesso motivo in chiusura: il backdrop/bottone dell'alert può restare focused
    // durante la rimozione dal DOM.
    this.blurActiveElement();
    return role === 'confirm';
  }

  /**
   * Avviso con più alternative oltre ad Annulla (feature 154). Restituisce il `value` della scelta
   * premuta, oppure `null` con Annulla o chiusura dal backdrop. Nessuna scelta è predefinita.
   */
  async presentChoiceAlert<T extends string>(title: string, message: string, scelte: SceltaAlert<T>[],
                                             cancelBtn = 'Annulla'): Promise<T | null> {
    this.blurActiveElement();
    const alert = await this.alertController.create({
      header: title,
      message,
      buttons: [
        {text: cancelBtn, role: 'cancel'},
        ...scelte.map(scelta => ({
          text: scelta.text,
          role: scelta.value,
          cssClass: scelta.cssClass ? `alert-button-confirm-${scelta.cssClass}` : undefined
        }))
      ]
    });
    await alert.present();

    const {role} = await alert.onDidDismiss();
    this.blurActiveElement();
    return scelte.find(scelta => scelta.value === role)?.value ?? null;
  }

  async presentTypedConfirmation(message: string, expectedText: string): Promise<TypedConfirmationResult> {
    this.blurActiveElement();
    const alert = await this.alertController.create({
      header: 'Eliminazione definitiva',
      message,
      inputs: [
        {
          name: 'confirmation',
          type: 'text',
          placeholder: expectedText,
          attributes: {autocapitalize: 'characters', autocomplete: 'off'}
        }
      ],
      buttons: [
        {text: 'Annulla', role: 'cancel'},
        {text: 'Elimina definitivamente', role: 'confirm', cssClass: 'alert-button-confirm-danger'}
      ],
      cssClass: 'danger'
    });
    await alert.present();

    const {role, data} = await alert.onDidDismiss();
    this.blurActiveElement();
    if (role !== 'confirm') return 'cancelled';

    return String(data?.values?.confirmation ?? '').trim().toLocaleUpperCase() === expectedText.toLocaleUpperCase()
      ? 'confirmed'
      : 'invalid';
  }

  private blurActiveElement() {
    const active = document.activeElement as HTMLElement | null;
    const nestedActive = active?.shadowRoot?.activeElement as HTMLElement | null;

    nestedActive?.blur();
    active?.blur();
  }
}
